/**
 * Removes leftover ChatGPT/AI text and UI artifacts that got copy-pasted
 * into product descriptions, pages, or blog posts.
 *
 * This is a SAFE, TEXT-ONLY patch:
 * - It only edits text nodes (never deletes elements, never changes layout)
 * - It runs once after the page finishes loading
 * - If nothing matches on a page, it does nothing
 *
 * This is a stopgap for what's already live while you re-import the
 * cleaned product descriptions. It is not a replacement for that fix.
 *
 * HOW TO INSTALL:
 * 1. Shopify Admin -> Online Store -> Themes -> Edit Code
 * 2. In the "assets" folder, click "Add a new asset" -> "Create a blank file"
 * 3. Name it: remove-ai-text.js
 * 4. Paste this entire file's content in, save
 * 5. Open layout/theme.liquid, find the closing </body> tag, and just
 *    ABOVE it add this one line:
 *
 *    <script src="{{ 'remove-ai-text.js' | asset_url }}" defer></script>
 *
 * 6. Save theme.liquid
 */
(function () {
  'use strict';

  // Phrases to strip completely (whole-phrase removal, case-insensitive)
  var PHRASES_TO_REMOVE = [
    'chatgpt said:',
    'you said:',
    'as an ai language model,',
    'as an ai language model',
  ];

  // Standalone words/terms to strip (removed wherever they appear as text)
  var WORDS_TO_REMOVE = [
    'chatgpt',
    'gpt-4o',
    'gpt-4',
    'gpt-3.5',
    'openai',
  ];

  function buildRegex() {
    var all = PHRASES_TO_REMOVE.concat(WORDS_TO_REMOVE)
      .sort(function (a, b) { return b.length - a.length; }) // longest first
      .map(function (s) {
        return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      });
    return new RegExp('(' + all.join('|') + ')', 'gi');
  }

  var MATCH_RE = buildRegex();

  function cleanTextNode(node) {
    var original = node.nodeValue;
    if (!original || original.indexOf(' ') === -1 && original.length < 4) return;
    if (!MATCH_RE.test(original)) return;
    MATCH_RE.lastIndex = 0;

    var cleaned = original.replace(MATCH_RE, '');
    // collapse leftover double spaces / stray punctuation created by removal
    cleaned = cleaned.replace(/\s{2,}/g, ' ').replace(/^[\s:.\-–—]+/, '');

    if (cleaned.trim() === '') {
      // The whole text node was just the AI label (e.g. "ChatGPT said:")
      // Remove the node itself, and if its parent is now empty, remove that too.
      var parent = node.parentNode;
      node.parentNode.removeChild(node);
      if (parent && parent.childNodes.length === 0 && parent.textContent.trim() === '') {
        // Only remove tiny leftover wrapper elements (h6/span/small), never
        // structural containers, to stay 100% safe.
        var tag = parent.tagName ? parent.tagName.toLowerCase() : '';
        if (['h6', 'span', 'small', 'em', 'strong', 'b', 'i'].indexOf(tag) !== -1) {
          parent.parentNode && parent.parentNode.removeChild(parent);
        }
      }
    } else if (cleaned !== original) {
      node.nodeValue = cleaned;
    }
  }

  function walk(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        var p = n.parentNode;
        if (!p) return NodeFilter.FILTER_REJECT;
        var tag = p.tagName ? p.tagName.toLowerCase() : '';
        // Skip script/style/textarea/input — never touch code or form values
        if (['script', 'style', 'textarea', 'input', 'noscript'].indexOf(tag) !== -1) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    var nodes = [];
    var current;
    while ((current = walker.nextNode())) {
      nodes.push(current);
    }
    nodes.forEach(cleanTextNode);
  }

  function run() {
    try {
      walk(document.body);
    } catch (e) {
      // Fail silently — never break the storefront over a text cleanup pass
      if (window.console) console.warn('remove-ai-text: skipped due to', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
